import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api, ApiClientError } from '../services/api';
import type { Message, Requirements, SessionState } from '../types';

const localMessageId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;

interface DesignerStore {
  sessionId: string | null;
  sessionToken: string | null;
  userToken: string | null;
  accountUser: { id: string; name: string; phone: string } | null;
  state: SessionState | null;
  busy: boolean;
  error: string | null;

  ensureSession: () => Promise<SessionState>;
  refresh: () => Promise<SessionState>;

  applyMessageOutcome: (params: {
    userContent: string;
    reply: string;
    requirements?: Partial<Requirements> | null;
    suggestedReplies?: string[] | null;
  }) => void;

  setUserToken: (token: string) => void;
  setAccountUser: (user: { id: string; name: string; phone: string } | null) => void;
  clearUserToken: () => void;
  setBusy: (value: boolean) => void;
  setError: (value: string | null) => void;
  reset: () => void;
}

let ensureSessionInFlight: Promise<SessionState> | null = null;
let sessionEpoch = 0;

const sessionRetryDelays = [250, 500, 1000, 1500];

const normalizeSession = (state: SessionState): SessionState => ({
  ...state,
  messages: Array.isArray(state.messages) ? state.messages : [],
  recommendations: Array.isArray(state.recommendations)
    ? state.recommendations
    : [],
  generations: Array.isArray(state.generations) ? state.generations : [],
  limits: state.limits ?? null,
  suggestedReplies: normalizeSuggestedReplies(state.suggestedReplies),
});

const normalizeSuggestedReplies = (
  suggestedReplies?: string[] | null,
): string[] => {
  if (!Array.isArray(suggestedReplies)) {
    return [];
  }

  return [...new Set(
    suggestedReplies
      .map((item) => item.trim())
      .filter((item) => item.length > 0),
  )].slice(0, 4);
};

async function createSessionWithRetry() {
  let lastError: unknown;

  for (
    let attempt = 0;
    attempt <= sessionRetryDelays.length;
    attempt += 1
  ) {
    try {
      return await api.createSession();
    } catch (error) {
      lastError = error;

      const retryable =
        !(error instanceof ApiClientError) ||
        !error.status ||
        error.status >= 500;

      if (!retryable || attempt === sessionRetryDelays.length) {
        throw error;
      }

      await new Promise((resolve) =>
        window.setTimeout(resolve, sessionRetryDelays[attempt]),
      );
    }
  }

  throw lastError;
}

export const useDesignerStore = create<DesignerStore>()(
  persist(
    (set, get) => ({
      sessionId: null,
      sessionToken: null,
      userToken: null,
      accountUser: null,
      state: null,
      busy: false,
      error: null,

      ensureSession: async () => {
        if (ensureSessionInFlight) {
          return ensureSessionInFlight;
        }

        const epoch = sessionEpoch;

        const request = (async () => {
          const current = get();

          if (current.sessionId && current.sessionToken) {
            try {
              const state = normalizeSession(
                await api.getSession(
                  current.sessionId,
                  current.sessionToken,
                ),
              );

              if (epoch === sessionEpoch) {
                set({
                  state,
                  error: null,
                });
              }

              return state;
            } catch {
              if (epoch === sessionEpoch) {
                set({
                  sessionId: null,
                  sessionToken: null,
                  userToken: null,
                  state: null,
                });
              }
            }
          }

          const created = await createSessionWithRetry();
          const state = normalizeSession(created.session);

          if (epoch === sessionEpoch) {
            set({
              sessionId: state.id,
              sessionToken: created.token,
              state,
              error: null,
            });
          }

          return state;
        })();

        ensureSessionInFlight = request;

        try {
          return await request;
        } finally {
          if (ensureSessionInFlight === request) {
            ensureSessionInFlight = null;
          }
        }
      },

      refresh: async () => {
        const { sessionId, sessionToken } = get();

        if (!sessionId || !sessionToken) {
          throw new Error('جلسه ساخته نشده است.');
        }

        const state = normalizeSession(
          await api.getSession(sessionId, sessionToken),
        );

        set({
          state,
          error: null,
        });

        return state;
      },

      applyMessageOutcome: ({
        userContent,
        reply,
        requirements,
        suggestedReplies,
      }) => {
        const { state } = get();

        if (!state) {
          return;
        }

        const now = new Date().toISOString();

        const userMessage: Message = {
          id: localMessageId(),
          role: 'user',
          content: userContent,
          createdAt: now,
        };

        const assistantMessage: Message = {
          id: localMessageId(),
          role: 'assistant',
          content: reply,
          createdAt: now,
        };

        set({
          state: {
            ...state,
            messages: [
              ...state.messages,
              userMessage,
              assistantMessage,
            ],
            requirements: requirements
              ? {
                ...(state.requirements ?? {
                  version: 0,
                  status: 'DRAFT',
                  features: [],
                  preferredCategories: [],
                }),
                ...requirements,
              }
              : state.requirements,
            suggestedReplies:
              normalizeSuggestedReplies(suggestedReplies),
          },
        });
      },

      setUserToken: (userToken) => set({ userToken }),

      setAccountUser: (accountUser) => set({ accountUser }),

      clearUserToken: () => set({ userToken: null, accountUser: null }),

      setBusy: (busy) => set({ busy }),

      setError: (error) => set({ error }),

      reset: () => {
        sessionEpoch += 1;
        ensureSessionInFlight = null;

        set({
          sessionId: null,
          sessionToken: null,
          userToken: null,
          accountUser: null,
          state: null,
          error: null,
          busy: false,
        });
      },
    }),
    {
      name: 'karolight-designer-session',
      partialize: (state) => ({
        sessionId: state.sessionId,
        sessionToken: state.sessionToken,
        userToken: state.userToken,
        accountUser: state.accountUser,
      }),
    },
  ),
);
